import { useQuery } from "@tanstack/react-query";

import CaseService from "services/Case";
import ContactService from "services/Contact";
import EntityService from "services/Entity";
import RevenueOpportunityService from "services/RevenueOpportunities";

// A note owned by one of these records can also surface on its parent entity's
// timeline. Each owner points at its entity through a single field, but none of
// their detail endpoints returns the entity's name, so the name is resolved in
// a second step.
const PARENT_ENTITY_ID_FETCHERS = {
  Contact: async modelId =>
    (await ContactService.getContact(modelId)).entity_id,
  Case: async modelId => (await CaseService.get(modelId)).entity_id,
  RevenueOpportunity: async modelId =>
    (await RevenueOpportunityService.getRevenueOpportunityById(modelId))
      .entity_id,
};

/**
 * Resolves the parent entity of a note's owning record.
 *
 * A Case may have no entity, in which case `entityId` is null and there is
 * nothing to surface the note on.
 *
 * @param {string} modelType - The note owner's model type.
 * @param {number} modelId - The note owner's id.
 * @returns {{ entityId: number|null, entityName: string|null, isLoading: boolean }}
 */
export function useNoteParentEntity(modelType, modelId) {
  const canResolve =
    !!modelId &&
    Object.prototype.hasOwnProperty.call(PARENT_ENTITY_ID_FETCHERS, modelType);

  const { data: entityId = null, isLoading: isLoadingOwner } = useQuery({
    queryKey: ["note-parent-entity-id", modelType, modelId],
    queryFn: () => PARENT_ENTITY_ID_FETCHERS[modelType](modelId),
    enabled: canResolve,
  });

  const { data: entityName = null, isLoading: isLoadingEntity } = useQuery({
    queryKey: ["note-parent-entity-name", entityId],
    queryFn: async () => (await EntityService.getEntity(entityId)).name,
    enabled: !!entityId,
  });

  const isLoading =
    canResolve && (isLoadingOwner || (!!entityId && isLoadingEntity));

  return { entityId, entityName, isLoading };
}
